import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Edit2,
  Info,
  X,
  Zap,
} from "lucide-react";
import api from "../services/api";
import { Button } from "./Button";
import { US_STATES, getAllCountryOptions } from "../utils/countries";
import { shouldShowRegionPicker, defaultRegionId, regionCountryCodes } from "../utils/regions";
import { useGoogleMapsLoader } from "../hooks/useGoogleMapsLoader";
import { useAddressAutocomplete } from "../hooks/useAddressAutocomplete";

// ── CAPTCHA helpers (same implementation as Signup page) ─────────────────────
const CAPTCHA_CHARSET = 'ABCDEFGHJKLMNPQRTUVWXY346789';
const CAPTCHA_LENGTH  = 6;
const CAPTCHA_CHAR_COLORS = [
  '#1a3a5c', '#243068', '#1e3c62', '#2a3870', '#163254', '#1c2e58',
];
const buildCharData = () =>
  Array.from({ length: CAPTCHA_LENGTH }, (_, i) => ({
    rotation : (Math.random() - 0.5) * 38,
    yOffset  : (Math.random() - 0.5) * 14,
    fontSize : 21 + Math.random() * 7,
    colorIdx : i % CAPTCHA_CHAR_COLORS.length,
  }));
const buildNoiseLines = () =>
  Array.from({ length: 5 }, () => ({
    x1: Math.random() * 240, y1: Math.random() * 72,
    x2: Math.random() * 240, y2: Math.random() * 72,
  }));
const generateCaptcha = () => {
  let chars = '';
  for (let i = 0; i < CAPTCHA_LENGTH; i++) {
    chars += CAPTCHA_CHARSET[Math.floor(Math.random() * CAPTCHA_CHARSET.length)];
  }
  return {
    captchaQuestion  : chars,
    captchaExpected  : chars.toUpperCase(),
    captchaCharData  : buildCharData(),
    captchaNoiseLines: buildNoiseLines(),
    captchaAnswer    : '',
  };
};

const CaptchaImage = ({ chars, charData, noiseLines }) => {
  const W = 240, H = 72, PAD_X = 20;
  const charStep = (W - PAD_X * 2) / (chars.length || 1);
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}
      xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ display: 'block' }}>
      <rect width={W} height={H} fill="#e8eef7" />
      {(noiseLines || []).map((l, i) => (
        <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
          stroke="#b8cde4" strokeWidth={0.85} opacity={0.6} />
      ))}
      {(chars || '').split('').map((ch, i) => {
        const d = (charData || [])[i] || {};
        const cx = PAD_X + i * charStep + charStep / 2;
        const cy = H / 2 + (d.yOffset || 0);
        return (
          <text key={i} x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
            fontSize={d.fontSize || 24} fontWeight="700"
            fill={CAPTCHA_CHAR_COLORS[d.colorIdx ?? (i % CAPTCHA_CHAR_COLORS.length)]}
            transform={`rotate(${d.rotation || 0}, ${cx}, ${cy})`}
            fontFamily="Georgia, 'Times New Roman', serif"
            style={{ userSelect: 'none' }}>
            {ch}
          </text>
        );
      })}
    </svg>
  );
};

const CIDR_REGEX = /^(\d{1,3}\.){3}\d{1,3}(\/([0-9]|[1-2][0-9]|3[0-2]))?$/;

// ── Stepper ──────────────────────────────────────────────────────────────────
const Stepper = ({ step }) => {
  const steps = [
    { n: 1, label: 'Details' },
    { n: 2, label: 'Review' },
  ];
  return (
    <div className="flex items-center gap-3">
      {steps.map((s, i) => {
        const active = step === s.n;
        const done   = step > s.n;
        return (
          <React.Fragment key={s.n}>
            <div className="flex items-center gap-2">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                  active ? 'bg-gradient-primary text-content-inverse'
                  : done  ? 'bg-status-success text-white'
                          : 'bg-theme-tertiary text-content-muted'
                }`}
              >
                {done ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : s.n}
              </div>
              <span className={`text-xs font-semibold ${
                active || done ? 'text-content-primary' : 'text-content-muted'
              }`}>
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`w-8 h-px ${done ? 'bg-status-success' : 'bg-border'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ── Review blocks ────────────────────────────────────────────────────────────
const ReviewSection = ({ title, subtitle, onEdit, children }) => (
  <div className="mb-4">
    <div className="flex items-center justify-between mb-2">
      <div>
        <div
          className="text-xs font-bold uppercase text-content-muted"
          style={{ letterSpacing: '0.06em' }}
        >
          {title}
        </div>
        {subtitle && <div className="text-xs mt-0.5 text-content-muted">{subtitle}</div>}
      </div>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="text-xs font-semibold flex items-center gap-1 text-accent-cyan hover:underline"
        >
          <Edit2 className="w-3 h-3" />
          Edit
        </button>
      )}
    </div>
    <div className="rounded-lg border border-border divide-y divide-border bg-theme-secondary">
      {children}
    </div>
  </div>
);

const ReviewRow = ({ label, value, strong }) => (
  <div className="flex items-start justify-between px-3 py-2">
    <span className="text-xs text-content-secondary">{label}</span>
    <span
      className={`text-xs text-right max-w-[60%] text-content-primary ${
        strong ? 'font-semibold' : ''
      }`}
    >
      {value}
    </span>
  </div>
);

// ── Submitted confirmation (Step 3) ──────────────────────────────────────────
const SubmittedConfirmation = ({ orgName, onClose, onViewInList }) => {
  const [elapsed, setElapsed] = useState(0);
  const [countdown, setCountdown] = useState(3);

  // Keep onClose in a ref so the timer-setup effect can run only once on mount.
  // Without this, every parent re-render (the auto-refresh countdown in
  // TenantSelection ticks every 1s) would clear and recreate the intervals
  // before they ever fired — the modal would appear frozen and never close.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    const tick = setInterval(() => setElapsed((e) => e + 1), 1000);
    const down = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    const autoClose = setTimeout(() => onCloseRef.current?.(), 3000);
    return () => {
      clearInterval(tick);
      clearInterval(down);
      clearTimeout(autoClose);
    };
  }, []);

  const mm = String(Math.floor(elapsed / 60));
  const ss = String(elapsed % 60).padStart(2, '0');

  return (
    <div
      className="bg-theme-secondary border border-border rounded-2xl shadow-theme-xl p-8 text-center"
      style={{ width: 460 }}
    >
      <div className="relative w-16 h-16 mx-auto mb-5">
        <div className="absolute inset-0 rounded-full bg-gradient-primary opacity-10" />
        <div className="absolute inset-2 rounded-full bg-gradient-primary opacity-15" />
        <div className="absolute inset-4 rounded-full flex items-center justify-center bg-gradient-primary">
          <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
        </div>
      </div>
      <h2 className="text-lg font-bold text-content-primary mb-2">
        {orgName} is being created
      </h2>
      <p className="text-sm leading-relaxed text-content-secondary mb-5">
        Provisioning schemas across this installation&apos;s regions. This may take a minute or two — you can stay
        here or close this window and we&apos;ll notify you when it&apos;s done.
      </p>
      <div className="mb-5 p-3 rounded-lg bg-theme-primary text-left">
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="text-content-secondary">Progress</span>
          <span className="font-mono text-content-muted">{mm}:{ss} elapsed</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden bg-theme-tertiary">
          <div
            className="h-full rounded-full relative overflow-hidden bg-gradient-primary"
            style={{ width: '18%' }}
          >
            <div className="shimmer" />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center gap-2">
        <Button variant="secondary" size="md" onClick={onViewInList}>View Organizations</Button>
        <Button variant="primary" size="md" onClick={onClose}>Got it</Button>
      </div>
      <p className="text-xs mt-4 text-content-muted">
        Closes automatically in {countdown}s
      </p>
    </div>
  );
};

// ── Main wizard ──────────────────────────────────────────────────────────────
const EMPTY_ORG = {
  organizationName: "",
  companySize: "",
  annualRevenue: "",
  organizationAddress1: "",
  organizationAddress2: "",
  organizationCity: "",
  organizationState: "",
  organizationZip: "",
  organizationContactNo1: "",
  organizationContactNo2: "",
  organizationCountry: "",
  regionId: "",
  verticalId: "",
  subVerticalId: "",
  tenantId: "",
  allowedIpRanges: [],
};

const NewOrganizationWizard = ({
  isOpen,
  onClose,
  ownerTenants,
  regions,
  verticals,
  user,
  onCreated,
}) => {
  const [step, setStep] = useState(1);
  const [newOrg, setNewOrg] = useState(EMPTY_ORG);
  const [captcha, setCaptcha] = useState(() => generateCaptcha());
  const [captchaError, setCaptchaError] = useState(null);
const [saving, setSaving] = useState(false);
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [submittedOrgName, setSubmittedOrgName] = useState('');
  const [focusField, setFocusField] = useState(null);

  // Reset everything when the wizard is closed (so a reopen starts clean, while
  // preserving the tenant choice if the user only owns one tenant).
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setNewOrg(EMPTY_ORG);
      setCaptcha(generateCaptcha());
      setCaptchaError(null);
      setSaving(false);
      setShowSubmitted(false);
      setSubmittedOrgName('');
    }
  }, [isOpen]);

  // Auto-select the only tenant the user owns.
  useEffect(() => {
    if (ownerTenants.length === 1) {
      setNewOrg((prev) =>
        prev.tenantId === ownerTenants[0].tenantId
          ? prev
          : { ...prev, tenantId: ownerTenants[0].tenantId }
      );
    }
  }, [ownerTenants]);

  // ── Google Maps / Address autocomplete ──────────────────────────────────
  const { isLoaded: googleIsLoaded, loadError: googleLoadError, isInactive: googleMapsInactive } = useGoogleMapsLoader();

  const handleBatchChange = useCallback((fields) => {
    setNewOrg((prev) => {
      const updates = {};
      Object.entries(fields).forEach(([k, v]) => {
        if (v !== undefined) updates[k] = v;
      });
      return { ...prev, ...updates };
    });
  }, []);

  // Only configured regions are listed; with one, the picker is hidden and that region is used
  // (also after a reset on close, which clears regionId). The wizard keeps regionId as a number.
  useEffect(() => {
    const id = defaultRegionId(regions, newOrg.regionId);
    const next = id === '' ? '' : Number(id);
    if (next !== newOrg.regionId) {
      setNewOrg((prev) => ({ ...prev, regionId: next }));
    }
  }, [regions, newOrg.regionId]);

  const showRegionPicker   = shouldShowRegionPicker(regions);
  const selectedRegion     = regions.find((r) => String(r.regionId) === String(newOrg.regionId));
  const selectedRegionName = selectedRegion?.regionName ?? '';

  // Memoised on countryFilterKey (not the display name, which an operator may override): a new
  // array each render would loop useAddressAutocomplete's effect.
  const regionCodes = useMemo(
    () => regionCountryCodes(selectedRegion),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRegion?.countryFilterKey]
  );

  const countryOptions = getAllCountryOptions();

  const {
    suggestions,
    showSuggestions,
    setShowSuggestions,
    handleSuggestionSelect,
    zipLoading,
  } = useAddressAutocomplete({
    isLoaded:     googleIsLoaded,
    addressValue: newOrg.organizationAddress1,
    country:      newOrg.organizationCountry,
    regionCodes,
    zip:          newOrg.organizationZip,
    city:         newOrg.organizationCity,
    state:        newOrg.organizationState,
    onBatchChange: handleBatchChange,
  });

  const addressWrapperRef = useRef(null);
  useEffect(() => {
    const close = (e) => {
      if (addressWrapperRef.current && !addressWrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [setShowSuggestions]);

  // Snap the scrollable body back to the top whenever the step changes, so
  // the user starts reading from the top of Step 2 instead of wherever they
  // were scrolled in Step 1. The focusField effect below will scrollIntoView
  // afterward when an Edit link sends the user back to a specific field.
  const bodyRef = useRef(null);
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [step]);

  // Focus a requested field after returning to Step 1 via the Edit link.
  useEffect(() => {
    if (step !== 1 || !focusField) return;
    const el = document.querySelector(`[name="${focusField}"]`);
    if (el) {
      el.focus();
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setFocusField(null);
  }, [step, focusField]);

  // ── Derived ─────────────────────────────────────────────────────────────
  const subVerticals =
    verticals.find((v) => v.verticalId === newOrg.verticalId)?.subVerticals || [];

  const verticalName =
    verticals.find((v) => v.verticalId === newOrg.verticalId)?.verticalName || '';
  const subVerticalName =
    subVerticals.find((s) => s.subVerticalId === newOrg.subVerticalId)?.subVerticalName || '';

  const countryLabel =
    countryOptions.find((o) => o.value === newOrg.organizationCountry)?.label ||
    newOrg.organizationCountry;

  const addressSummary = [
    newOrg.organizationAddress1,
    newOrg.organizationAddress2,
    [newOrg.organizationCity, newOrg.organizationState, newOrg.organizationZip]
      .filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');

  const phoneSummary = [newOrg.organizationContactNo1, newOrg.organizationContactNo2]
    .filter(Boolean).join(', ');

  // Required-fields gate for Step 1 → Step 2
  const step1Valid =
    newOrg.organizationName.trim() &&
    newOrg.regionId &&
    newOrg.organizationCountry &&
    newOrg.verticalId &&
    newOrg.subVerticalId &&
    (ownerTenants.length <= 1 || newOrg.tenantId);

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "regionId") {
      setNewOrg((prev) => ({
        ...prev,
        regionId: value === "" ? "" : Number(value),
        organizationState: "",
      }));
    } else if (name === "verticalId") {
      setNewOrg((prev) => ({
        ...prev,
        verticalId: value === "" ? "" : Number(value),
        subVerticalId: "",
      }));
    } else if (name === "organizationCountry") {
      setNewOrg((prev) => ({ ...prev, organizationCountry: value, organizationState: "" }));
    } else if (name === "organizationZip" || name === "organizationCity" || name === "organizationState") {
      setNewOrg((prev) => ({ ...prev, [name]: value, organizationAddress1: "" }));
    } else if (name === "tenantId" || name === "subVerticalId") {
      setNewOrg((prev) => ({ ...prev, [name]: value === "" ? "" : Number(value) }));
    } else {
      setNewOrg((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAddIpRange = () =>
    setNewOrg((prev) => ({ ...prev, allowedIpRanges: [...prev.allowedIpRanges, ""] }));

  const handleIpRangeChange = (index, value) =>
    setNewOrg((prev) => {
      const updated = [...prev.allowedIpRanges];
      updated[index] = value;
      return { ...prev, allowedIpRanges: updated };
    });

  const handleRemoveIpRange = (index) =>
    setNewOrg((prev) => ({
      ...prev,
      allowedIpRanges: prev.allowedIpRanges.filter((_, i) => i !== index),
    }));

  const handleCaptchaChange = (e) => {
    setCaptcha((prev) => ({ ...prev, captchaAnswer: e.target.value }));
    setCaptchaError(null);
  };
  const handleRefreshCaptcha = () => {
    setCaptcha(generateCaptcha());
    setCaptchaError(null);
  };
  const handleCaptchaAudio = () => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const text = (captcha.captchaQuestion || '').split('').join(' … ');
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.6;
    utterance.pitch = 1;
    window.speechSynthesis.speak(utterance);
  };

  const handleContinue = () => {
    if (!step1Valid) return;
    const invalidIps = newOrg.allowedIpRanges.filter(
      (ip) => ip.trim() && !CIDR_REGEX.test(ip.trim())
    );
    if (invalidIps.length > 0) {
      alert(`Invalid IP range format: ${invalidIps.join(", ")}\nUse CIDR notation (e.g. 192.168.1.0/24) or a plain IP.`);
      return;
    }
    setStep(2);
  };

  const handleBack = () => setStep(1);

  const handleEdit = (field) => {
    setFocusField(field);
    setStep(1);
  };

  const handleCreate = async () => {
    if (!captcha.captchaAnswer || captcha.captchaAnswer.toUpperCase() !== captcha.captchaExpected) {
      setCaptchaError("Incorrect characters — please try again.");
      setCaptcha({ ...generateCaptcha(), captchaAnswer: '' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        organizationName:       newOrg.organizationName,
        companySize:            newOrg.companySize    || null,
        annualRevenue:          newOrg.annualRevenue  || null,
        allowedIpRanges:        newOrg.allowedIpRanges.filter((ip) => ip.trim()).join(",") || null,
        organizationAddress1:   newOrg.organizationAddress1,
        organizationAddress2:   newOrg.organizationAddress2,
        organizationCity:       newOrg.organizationCity    || null,
        organizationState:      newOrg.organizationState   || null,
        organizationZip:        newOrg.organizationZip     || null,
        organizationContactNo1: newOrg.organizationContactNo1,
        organizationContactNo2: newOrg.organizationContactNo2,
        organizationCountry:    newOrg.organizationCountry,
        regionId:               Number(newOrg.regionId),
        verticalId:             Number(newOrg.verticalId),
        subVerticalId:          Number(newOrg.subVerticalId),
        tenantId:               Number(newOrg.tenantId),
      };

      const response = await api.post("/Organizations", payload);
      const created = response.data;

      setSubmittedOrgName(created.organizationName || newOrg.organizationName);
      setShowSubmitted(true);
      onCreated?.(created);
    } catch (error) {
      alert("Failed to create organization. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Shared input classes — match existing TenantSelection styling
  const inputCls = "block w-full p-2 bg-input-bg border border-border rounded-lg text-content-primary placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan text-sm";
  const labelCls = "block text-xs font-medium text-content-secondary mb-1.5";

  // ── Submitted confirmation — centered smaller modal ─────────────────────
  if (showSubmitted) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-overlay backdrop-blur-sm z-50 p-4">
        <SubmittedConfirmation
          orgName={submittedOrgName}
          onClose={onClose}
          onViewInList={onClose}
        />
      </div>
    );
  }

  // ── Wizard shell ────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-overlay backdrop-blur-sm z-50 p-4">
      <div
        className="bg-theme-secondary border border-border rounded-2xl shadow-theme-xl flex flex-col w-full relative overflow-hidden"
        style={{ maxWidth: 560, maxHeight: '90vh' }}
      >
        {saving && (
          <div className="absolute inset-0 bg-theme-secondary/75 flex items-center justify-center rounded-2xl z-20">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-transparent border-t-accent-cyan border-r-accent-purple" />
          </div>
        )}

        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-border">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h2 className="text-lg font-bold text-content-primary">
                {step === 1 ? 'New Organization' : 'Review & Confirm'}
              </h2>
              <p className="text-xs mt-0.5 text-content-secondary">
                {step === 1
                  ? 'Basic details about the organization'
                  : 'Make sure these details are correct before we create your organization.'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-content-muted hover:text-content-primary"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <Stepper step={step} />
        </div>

        {/* Body */}
        <div ref={bodyRef} className="px-6 py-5 overflow-y-auto" style={{ maxHeight: 520 }}>
          {step === 1 ? (
            <div className="space-y-3.5">
              {ownerTenants.length > 1 && (
                <div>
                  <label className={labelCls}>Tenant <span className="text-risk-critical">*</span></label>
                  <select
                    name="tenantId"
                    value={newOrg.tenantId}
                    onChange={handleInputChange}
                    className={inputCls}
                  >
                    <option value="">-- Select Tenant --</option>
                    {ownerTenants.map((t) => (
                      <option key={t.tenantId} value={t.tenantId}>{t.tenantName}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className={labelCls}>Organization Name <span className="text-risk-critical">*</span></label>
                <input
                  type="text"
                  name="organizationName"
                  value={newOrg.organizationName}
                  onChange={handleInputChange}
                  placeholder="Enter your organization name"
                  className={inputCls}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Company Size</label>
                  <select name="companySize" value={newOrg.companySize} onChange={handleInputChange} className={inputCls}>
                    <option value="">-- Select Company Size --</option>
                    <option value="small">Small (1–50 employees)</option>
                    <option value="medium">Medium (51–500 employees)</option>
                    <option value="large">Large (501–5,000 employees)</option>
                    <option value="enterprise">Enterprise (5,000+ employees)</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Annual Revenue</label>
                  <select name="annualRevenue" value={newOrg.annualRevenue} onChange={handleInputChange} className={inputCls}>
                    <option value="">-- Select Annual Revenue --</option>
                    <option value="<1M">Less than $1M</option>
                    <option value="1-10M">$1M – $10M</option>
                    <option value="10-50M">$10M – $50M</option>
                    <option value="50-100M">$50M – $100M</option>
                    <option value=">100M">More than $100M</option>
                  </select>
                </div>
              </div>

              {showRegionPicker && (
                <div>
                  <label className={labelCls}>Data Residency Region <span className="text-risk-critical">*</span></label>
                  <select name="regionId" value={newOrg.regionId} onChange={handleInputChange} className={inputCls}>
                    <option value="">-- Select Region --</option>
                    {regions.map((r) => (
                      <option key={r.regionId} value={r.regionId}>{r.regionName}</option>
                    ))}
                  </select>
                  <p className="text-xs text-content-muted mt-1">
                    Primary data for this organization lives in this region.
                  </p>
                </div>
              )}

              <div>
                <label className={labelCls}>Country <span className="text-risk-critical">*</span></label>
                <select name="organizationCountry" value={newOrg.organizationCountry} onChange={handleInputChange} className={inputCls}>
                  {countryOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div ref={addressWrapperRef} className="relative">
                <label className={labelCls}>Street Address</label>
                <input
                  type="text"
                  name="organizationAddress1"
                  value={newOrg.organizationAddress1}
                  onChange={handleInputChange}
                  autoComplete="off"
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  placeholder={googleLoadError || googleMapsInactive ? "Enter street address" : "Start typing to search address…"}
                  className={inputCls}
                />
                {showSuggestions && suggestions.length > 0 && (
                  <ul
                    className="absolute z-50 w-full bg-theme-secondary border border-border rounded-xl shadow-theme-xl overflow-hidden"
                    style={{ top: 'calc(100% + 2px)' }}
                  >
                    {suggestions.map((s, i) => (
                      <li key={s.placePrediction?.placeId || i} className="border-b border-border last:border-b-0">
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleSuggestionSelect(s)}
                          className="w-full px-4 py-2.5 text-left hover:bg-theme-primary transition-colors"
                        >
                          <p className="text-sm font-medium text-content-primary truncate">
                            {s.placePrediction?.mainText?.text}
                          </p>
                          {s.placePrediction?.secondaryText?.text && (
                            <p className="text-xs text-content-muted truncate mt-0.5">
                              {s.placePrediction.secondaryText.text}
                            </p>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {googleLoadError && (
                  <p className="text-xs text-content-muted mt-1">
                    Address autocomplete unavailable — please enter manually.
                  </p>
                )}
              </div>

              <div>
                <label className={labelCls}>Address 2</label>
                <input
                  type="text"
                  name="organizationAddress2"
                  value={newOrg.organizationAddress2}
                  onChange={handleInputChange}
                  placeholder="Apt, suite, unit, etc."
                  className={inputCls}
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className={labelCls}>ZIP / Postal</label>
                  <input
                    type="text"
                    name="organizationZip"
                    value={newOrg.organizationZip}
                    onChange={handleInputChange}
                    placeholder="ZIP"
                    className={inputCls}
                  />
                  {zipLoading && <p className="text-xs text-content-muted mt-0.5 animate-pulse">Looking up…</p>}
                </div>
                <div>
                  <label className={labelCls}>State / Province</label>
                  {newOrg.organizationCountry === 'US' ? (
                    <select
                      name="organizationState"
                      value={newOrg.organizationState}
                      onChange={handleInputChange}
                      className={inputCls}
                    >
                      <option value="">-- State --</option>
                      {US_STATES.map((s) => (
                        <option key={s.code} value={s.code}>{s.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      name="organizationState"
                      value={newOrg.organizationState}
                      onChange={handleInputChange}
                      placeholder="State / Province"
                      className={inputCls}
                    />
                  )}
                </div>
                <div>
                  <label className={labelCls}>City</label>
                  <input
                    type="text"
                    name="organizationCity"
                    value={newOrg.organizationCity}
                    onChange={handleInputChange}
                    placeholder="City"
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Vertical <span className="text-risk-critical">*</span></label>
                  <select name="verticalId" value={newOrg.verticalId} onChange={handleInputChange} className={inputCls}>
                    <option value="">-- Select Vertical --</option>
                    {verticals.map((v) => (
                      <option key={v.verticalId} value={v.verticalId}>{v.verticalName}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Sub-Vertical <span className="text-risk-critical">*</span></label>
                  <select
                    name="subVerticalId"
                    value={newOrg.subVerticalId}
                    onChange={handleInputChange}
                    disabled={!newOrg.verticalId}
                    className={`${inputCls} disabled:opacity-50`}
                  >
                    <option value="">-- Select Sub-Vertical --</option>
                    {subVerticals.map((sv) => (
                      <option key={sv.subVerticalId} value={sv.subVerticalId}>{sv.subVerticalName}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Phone No</label>
                  <input
                    type="text"
                    name="organizationContactNo1"
                    value={newOrg.organizationContactNo1}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 000-0000"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Phone No 2</label>
                  <input
                    type="text"
                    name="organizationContactNo2"
                    value={newOrg.organizationContactNo2}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 000-0000"
                    className={inputCls}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>Created By</label>
                <div className={`${inputCls} text-content-muted cursor-default select-none bg-theme-primary`}>
                  {user?.userName || user?.email || '—'}
                </div>
              </div>

              {/* Allowed IP Ranges */}
              <div className="pt-2">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <label className={labelCls}>
                      Allowed IP Ranges <span className="text-xs text-content-muted font-normal">(optional)</span>
                    </label>
                    <p className="text-xs text-content-muted mt-0.5">
                      If set, only requests from these IPs / CIDR ranges can access this organization.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddIpRange}
                    className="text-xs font-semibold text-accent-cyan hover:underline flex-shrink-0 ml-3"
                  >
                    + Add Range
                  </button>
                </div>
                {newOrg.allowedIpRanges.length === 0 ? (
                  <p className="text-xs text-content-muted italic py-2">No restrictions — all IPs allowed.</p>
                ) : (
                  <div className="space-y-2 mt-2">
                    {newOrg.allowedIpRanges.map((ip, index) => {
                      const invalid = ip && !CIDR_REGEX.test(ip.trim());
                      return (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={ip}
                            onChange={(e) => handleIpRangeChange(index, e.target.value)}
                            placeholder="e.g. 192.168.1.0/24 or 10.0.0.5"
                            autoComplete="off"
                            className={`${inputCls} flex-1 font-mono text-xs ${
                              invalid ? "border-risk-critical focus:ring-risk-critical/40 focus:border-risk-critical" : ""
                            }`}
                          />
                          {invalid && <span className="text-xs text-risk-critical flex-shrink-0">Mark Invalid</span>}
                          <button
                            type="button"
                            onClick={() => handleRemoveIpRange(index)}
                            className="flex-shrink-0 w-7 h-7 rounded-lg border border-border bg-theme-secondary hover:bg-theme-hover flex items-center justify-center transition-colors"
                            title="Remove"
                            aria-label="Remove"
                          >
                            <X className="w-3.5 h-3.5 text-content-muted" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // ── Step 2 ──────────────────────────────────────────────────
            (<div>
              <div className="mb-4 p-3.5 rounded-lg flex gap-3 bg-accent-cyan/5 border border-accent-cyan/20">
                <Info className="w-5 h-5 flex-shrink-0 mt-0.5 text-accent-cyan" />
                <div>
                  <p className="text-xs font-semibold text-content-primary">
                    Setting up your organization.
                  </p>
                  <p className="text-xs mt-1 leading-relaxed text-content-secondary">
                    This usually takes 1–2 minutes. We'll let you know as soon as it's ready.
                  </p>
                </div>
              </div>
              <ReviewSection title="Organization" onEdit={() => handleEdit('organizationName')}>
                <ReviewRow label="Name" value={newOrg.organizationName} strong />
                <ReviewRow label="Company Size" value={newOrg.companySize || '—'} />
                <ReviewRow label="Annual Revenue" value={newOrg.annualRevenue || '—'} />
                <ReviewRow label="Vertical" value={verticalName} />
                <ReviewRow label="Sub-Vertical" value={subVerticalName} />
                <ReviewRow
                  label="Data Residency"
                  value={
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium font-mono bg-accent-cyan/10 text-accent-cyan"
                    >
                      {selectedRegionName}
                    </span>
                  }
                />
              </ReviewSection>
              <ReviewSection title="Location" onEdit={() => handleEdit(showRegionPicker ? 'regionId' : 'organizationCountry')}>
                <ReviewRow label="Country" value={countryLabel || '—'} />
                <ReviewRow label="Address" value={addressSummary || '—'} />
                <ReviewRow label="Phone" value={phoneSummary || '—'} />
              </ReviewSection>
              <ReviewSection title="Security & Access" onEdit={() => handleEdit('allowedIpRanges')}>
                <ReviewRow
                  label="Allowed IP Ranges"
                  value={
                    newOrg.allowedIpRanges.filter((ip) => ip.trim()).length === 0 ? (
                      <span className="text-content-muted italic">No restrictions — all IPs allowed</span>
                    ) : (
                      <span className="font-mono">
                        {newOrg.allowedIpRanges.filter((ip) => ip.trim()).join(', ')}
                      </span>
                    )
                  }
                />
                <ReviewRow label="Created By" value={user?.userName || user?.email || '—'} />
              </ReviewSection>
              {/* CAPTCHA */}
              <div className="pt-2 mb-4">
                <label className={labelCls}>Type the characters in the image</label>
                <div className="flex gap-2 mb-2">
                  <div className="flex-1 rounded-lg overflow-hidden border border-border shadow-sm">
                    <CaptchaImage
                      chars={captcha.captchaQuestion}
                      charData={captcha.captchaCharData}
                      noiseLines={captcha.captchaNoiseLines}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <button
                      type="button"
                      onClick={handleRefreshCaptcha}
                      title="Get a new image"
                      aria-label="Refresh CAPTCHA"
                      className="w-9 h-9 rounded-lg border border-border bg-theme-secondary hover:bg-theme-primary flex items-center justify-center transition-colors"
                    >
                      <svg className="w-4 h-4 text-content-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={handleCaptchaAudio}
                      title="Hear the characters read aloud"
                      aria-label="Hear CAPTCHA"
                      className="w-9 h-9 rounded-lg border border-border bg-theme-secondary hover:bg-theme-primary flex items-center justify-center transition-colors"
                    >
                      <svg className="w-4 h-4 text-content-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15.536 8.464a5 5 0 010 7.072M12 6v12m0 0l-3-3m3 3l3-3M9 9a3 3 0 000 6" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728" />
                      </svg>
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  value={captcha.captchaAnswer}
                  onChange={handleCaptchaChange}
                  placeholder="Enter the characters above"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  className={`${inputCls}${captchaError ? " border-risk-critical focus:ring-risk-critical/40 focus:border-risk-critical" : ""}`}
                />
                {captchaError && <p className="text-xs text-risk-critical mt-1">{captchaError}</p>}
              </div>
            </div>)
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex items-center justify-between bg-theme-primary">
          <div className="flex items-center gap-1.5 text-xs text-content-muted">
            {step === 2 && (
              <>
                <Zap className="w-3.5 h-3.5" />
                <span>We'll get started as soon as you click Create</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="md" onClick={onClose}>Cancel</Button>
            {step === 1 ? (
              <Button
                variant="primary"
                size="md"
                onClick={handleContinue}
                disabled={!step1Valid}
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            ) : (
              <>
                <Button variant="secondary" size="md" onClick={handleBack}>
                  <ArrowLeft className="w-4 h-4 mr-1.5" />
                  Back
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleCreate}
                  disabled={!captcha.captchaAnswer || saving}
                >
                  Create
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewOrganizationWizard;
